/**
 * OAuth callback page at /auth/callback. Listener runs from index.tsx; we poll
 * for session, verify VetNebula user, sync role to Cognito, refresh token, then redirect to home.
 */
import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession } from "aws-amplify/auth";
import { AuthContainer } from "components/Layout/AuthContainer";
import { AdminRoutes } from "config/routes";
import { exchangeCognitoToken } from "services/accountSession";
import { LocalStorage } from "utils/LocalStorage";

const POLL_DELAYS_MS = [0, 500, 1000, 2000, 3000, 5000, 8000];
const GIVE_UP_MS = 12000;

function getOAuthErrorMessage(
  error: string,
  errorDescription: string | null,
): string {
  if (errorDescription) {
    return decodeURIComponent(errorDescription.replace(/\+/g, " "));
  }
  return `Sign-in failed: ${error}`;
}

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const gaveUp = useRef(false);
  const completing = useRef(false);

  useEffect(() => {
    const errorParam = searchParams.get("error");
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorDescription = searchParams.get("error_description");
    const redirectResponse = {
      url: typeof window !== "undefined" ? window.location.href : "",
      code: code ?? null,
      state: state ?? null,
      error: errorParam ?? null,
      error_description: errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
        : null,
    };
    if (process.env.NODE_ENV === "development") {
      console.log("[signInWithRedirect callback] redirect response:", {
        ...redirectResponse,
        url: redirectResponse.url ? "[redacted]" : "",
        code: redirectResponse.code ? "[redacted]" : null,
      });
    }

    if (errorParam) {
      setError(
        getOAuthErrorMessage(errorParam, searchParams.get("error_description")),
      );
      return;
    }

    let cancelled = false;

    const redirectIfSignedIn = async () => {
      if (cancelled || gaveUp.current || completing.current) return;
      try {
        const session = await fetchAuthSession();

        if (cancelled || gaveUp.current) return;
        if (session?.tokens?.idToken) {
          completing.current = true;
          const idToken = session.tokens.idToken;
          LocalStorage.setCognitoTokenExpirationTime(
            idToken.payload?.exp?.toString() || "0",
          );
          await exchangeCognitoToken(idToken.toString());

          if (cancelled || gaveUp.current) return;
          window.location.replace(AdminRoutes.Home);
        }
      } catch (authError: any) {
        completing.current = false;
        if (!cancelled && !gaveUp.current)
          setError(
            authError?.response?.status === 403
              ? "Unauthorized. No VetNebula account is available for this sign-in."
              : "Sign-in could not be completed.",
          );
      }
    };

    const hubListener = Hub.listen("auth", ({ payload }) => {
      if (cancelled) return;
      switch (payload.event) {
        case "signedIn":
        case "signInWithRedirect":
          redirectIfSignedIn();
          break;
        case "signInWithRedirect_failure":
          setError(
            (payload.data as { message?: string })?.message ??
              "Sign-in was cancelled or failed.",
          );
          break;
      }
    });

    const timeouts: number[] = [];
    POLL_DELAYS_MS.forEach((delayMs) => {
      const id = window.setTimeout(() => redirectIfSignedIn(), delayMs);
      timeouts.push(id);
    });

    const giveUpTimer = window.setTimeout(() => {
      gaveUp.current = true;
      if (!cancelled) {
        setError(
          "Sign-in is taking too long. Check that your Cognito app client has callback URL: " +
            window.location.origin +
            AdminRoutes.AuthCallback,
        );
      }
    }, GIVE_UP_MS);

    return () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(giveUpTimer);
      hubListener();
    };
  }, [searchParams]);

  if (error) {
    return (
      <AuthContainer loading={false}>
        <p>{error}</p>
        <a href="/">Return to sign in</a>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer loading loadingText="Completing sign in...">
      {null}
    </AuthContainer>
  );
}
